import React, { useState, useEffect, useCallback } from 'react';
import { registryHistoryAPI, skillsAPI, groupsAPI } from '../services/api';

const IANA_TZ_MAP = {
  '-180': 'America/Argentina/Buenos_Aires',
  '-240': 'America/Santiago',
  '-300': 'America/Bogota',
  '0': 'UTC',
};

const History = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [groups, setGroups] = useState([]);

  const [filterSince, setFilterSince] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterUsername, setFilterUsername] = useState('');

  const loadFilters = useCallback(async () => {
    try {
      const [s, g] = await Promise.all([
        skillsAPI.getSkills(),
        groupsAPI.getGroups(),
      ]);
      if (Array.isArray(s)) setSkills(s);
      if (Array.isArray(g)) setGroups(g);
    } catch (_) {}
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSince) params.since = new Date(filterSince).toISOString();
      if (filterStatus) params.status = filterStatus;
      if (filterSkill) params.skill = filterSkill;
      if (filterGroup) params.group_name = filterGroup;
      if (filterUsername) params.username = filterUsername;
      const data = await registryHistoryAPI.getHistory(params);
      if (Array.isArray(data)) setEntries(data);
    } catch (err) {
      console.error('Failed to load registry history:', err);
    } finally {
      setLoading(false);
    }
  }, [filterSince, filterStatus, filterSkill, filterGroup, filterUsername]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const tzOffset = localStorage.getItem('tzOffset');
    if (!tzOffset) return d.toLocaleString();
    const iana = IANA_TZ_MAP[tzOffset];
    if (!iana) return d.toLocaleString();
    return d.toLocaleString('en-US', { timeZone: iana });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, color: '#60a5fa' }}>Registry History</h2>
        <button onClick={loadHistory} style={{ backgroundColor: 'transparent', color: '#60a5fa', border: '1px solid #60a5fa', borderRadius: '8px', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>&#x21bb;</button>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Since</label>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <input type="datetime-local" value={filterSince} onChange={e => setFilterSince(e.target.value)} style={styles.input} />
            {filterSince && <span onClick={() => setFilterSince('')} style={{ cursor: 'pointer', color: '#ef4444', fontSize: '16px', fontWeight: 'bold', lineHeight: '1' }}>&#x2715;</span>}
          </div>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.select}>
            <option value="">All</option>
            <option value="AVAILABLE">Available</option>
            <option value="UNAVAILABLE">Unavailable</option>
            <option value="BUSY-ONCALL">Busy - OnCall</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Skill</label>
          <select value={filterSkill} onChange={e => setFilterSkill(e.target.value)} style={styles.select}>
            <option value="">All</option>
            {skills.map(s => <option key={s._id || s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Group</label>
          <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} style={styles.select}>
            <option value="">All</option>
            {groups.map(g => <option key={g._id || g.id} value={g.name}>{g.name}</option>)}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.label}>Username</label>
          <input type="text" placeholder="Filter by username" value={filterUsername} onChange={e => setFilterUsername(e.target.value)} style={styles.input} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Loading...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
                <tr>
                  <th style={styles.th}>Event Time</th>
                  <th style={styles.th}>Extension</th>
                  <th style={styles.th}>Username</th>
                  <th style={styles.th}>Event</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Skills</th>
                  <th style={styles.th}>Group</th>
                  <th style={styles.th}>UUID</th>
                </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                    No history records found
                  </td>
                </tr>
              ) : (
                entries.map(entry => {
                  const eventType = entry.event_type;
                  let eventStyle, eventLabel;
                  if (eventType === 'call_answered') {
                    eventStyle = { backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' };
                    eventLabel = 'Call Answered';
                  } else if (eventType === 'call_agent_hangup') {
                    eventStyle = { backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa' };
                    eventLabel = 'Agent Hangup';
                  } else if (eventType === 'call_caller_hangup') {
                    eventStyle = { backgroundColor: 'rgba(249, 115, 22, 0.2)', color: '#fb923c' };
                    eventLabel = 'Caller Hangup';
                  } else if (eventType === 'call_ringing') {
                    eventStyle = { backgroundColor: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
                    eventLabel = 'Call Ringing';
                  } else if (eventType === 'call_hungup') {
                    eventStyle = { backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' };
                    eventLabel = 'Call Hungup';
                  } else if (eventType === 'UNREGISTER') {
                    eventStyle = { backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
                    eventLabel = 'UNREGISTER';
                  } else {
                    eventStyle = { backgroundColor: 'rgba(96, 165, 250, 0.2)', color: '#60a5fa' };
                    eventLabel = eventType || 'Unknown';
                  }
                  const st = entry.status;
                  const statusColor = st === 'BUSY-ONCALL' ? '#fbbf24' : st === 'AVAILABLE' ? '#34d399' : st === 'AGENT-HANGUP' ? '#a78bfa' : st === 'CALLER-HANGUP' ? '#fb923c' : '#ef4444';
                  const statusBg = st === 'BUSY-ONCALL' ? 'rgba(251, 191, 36, 0.2)' : st === 'AVAILABLE' ? 'rgba(52, 211, 153, 0.2)' : st === 'AGENT-HANGUP' ? 'rgba(139, 92, 246, 0.2)' : st === 'CALLER-HANGUP' ? 'rgba(249, 115, 22, 0.2)' : 'rgba(239, 68, 68, 0.2)';

                  return (
                  <tr key={entry._id} style={{ opacity: entry.event_type === 'UNREGISTER' ? 0.7 : 1 }}>
                    <td style={styles.td}>{formatDate(entry.event_time)}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 600, color: '#e2e8f0' }}>{entry.extension}</td>
                    <td style={styles.td}>{entry.username || '-'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...eventStyle }}>
                        {eventLabel}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: statusBg, color: statusColor }}>
                        {entry.status || '-'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {entry.skills?.length > 0 ? (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {entry.skills.map((s, i) => (
                            <span key={i} style={{ ...styles.badge, backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }}>{s.name}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {entry.group_name ? (
                        <span style={{ ...styles.badge, backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' }}>{entry.group_name}</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={styles.td}>{entry.recording_uuid ? <span style={{ fontFamily: 'monospace', color: '#60a5fa' }}>{entry.recording_uuid}</span> : <span style={{ color: '#9ca3af' }}>-</span>}</td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles = {
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1.5rem',
    backgroundColor: '#1f2937',
    padding: '1rem',
    borderRadius: '12px',
    border: '1px solid #374151',
    alignItems: 'flex-end',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontWeight: '500', color: '#9ca3af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { padding: '0.5rem 0.75rem', border: '2px solid #374151', borderRadius: '8px', fontSize: '14px', background: '#111827', color: '#e2e8f0', outline: 'none', minWidth: '160px' },
  select: { padding: '0.5rem 0.75rem', border: '2px solid #374151', borderRadius: '8px', fontSize: '14px', background: '#111827', color: '#e2e8f0', outline: 'none', cursor: 'pointer', minWidth: '140px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#e2e8f0' },
  badge: { display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 },
};

export default History;
